import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Phone, Mail, MapPin, Inbox as InboxIcon, Loader2, Printer, Trash2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import AdminLabels from "@/pages/AdminLabels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "jsl_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(j.detail));
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Welcome back!");
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="blueprint flex min-h-screen items-center justify-center bg-paper p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-[24px] border border-ink/10 bg-white p-8 shadow-[0_30px_80px_rgba(22,50,79,0.18)] sm:p-10"
        data-testid="admin-login-card"
      >
        <div className="flex items-center gap-3">
          <LogoMark className="h-8 w-auto" />
          <span className="font-display text-lg font-extrabold tracking-tight text-ink">JSL DENTAL REPAIR</span>
        </div>
        <h1 className="font-display mt-6 text-2xl font-extrabold text-ink">Office login</h1>
        <p className="mt-1 text-[13.5px] text-slate-500">Sign in to view intake submissions.</p>

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="mb-1.5 block text-[12.5px] font-bold text-ink" htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              className="field"
              placeholder="you@jsldentalrepair.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[12.5px] font-bold text-ink" htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              className="field"
              placeholder="••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-lift flex w-full items-center justify-center gap-2.5 rounded-full bg-ink px-6 py-3.5 text-[14.5px] font-bold text-white shadow-[0_14px_34px_rgba(22,50,79,0.3)] hover:bg-abyss disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <a href="/" className="mt-6 block text-center text-[12.5px] font-semibold text-slate-400 hover:text-ink">
          ← Back to the website
        </a>
      </motion.div>
    </div>
  );
}

function IntakeCard({ intake, onStatus, onDelete }) {
  const st = intake.status || "new";
  return (
    <div
      className={`overflow-hidden rounded-[20px] border bg-white shadow-[0_8px_30px_rgba(22,50,79,0.07)] transition-all duration-300 ${
        st === "new" ? "border-brandpink/60 ring-2 ring-brandpink/15" : st === "to_do" ? "border-amber-400/60" : "border-brandleaf/50 opacity-80"
      }`}
      data-testid={`admin-intake-card-${intake.intake_id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 bg-cloud/50 px-5 py-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono rounded-lg bg-ink px-2.5 py-1 text-[11.5px] font-bold text-white">{intake.intake_id}</span>
          <span className="text-[13px] text-slate-400">{fmtDate(intake.created_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-brandsky/40 bg-brandsky-soft px-3 py-1 text-[11px] font-bold text-brandsky-deep">
            {intake.delivery_method}
          </span>
          <button
            onClick={() => onDelete(intake.intake_id)}
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
            aria-label={`Delete submission ${intake.intake_id}`}
            data-testid={`intake-delete-${intake.intake_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-ink/10 bg-cloud/30 px-5 py-2.5" data-testid={`admin-status-bar-${intake.intake_id}`}>
        <span className="eyebrow text-slate-400">Status</span>
        {[["new", "Not yet seen"], ["to_do", "To do"], ["completed", "Completed"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => onStatus(intake.intake_id, id)}
            data-testid={`intake-status-${id}-${intake.intake_id}`}
            className={`rounded-full px-3.5 py-1.5 text-[10.5px] font-extrabold uppercase tracking-wide transition-all duration-200 ${
              st === id
                ? id === "new"
                  ? "bg-brandpink text-white shadow-[0_6px_16px_rgba(234,76,137,0.35)]"
                  : id === "to_do"
                    ? "bg-amber-500 text-white shadow-[0_6px_16px_rgba(245,158,11,0.35)]"
                    : "bg-brandleaf text-white shadow-[0_6px_16px_rgba(127,193,108,0.35)]"
                : "border border-ink/10 bg-white text-slate-400 hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 px-5 py-4 sm:grid-cols-4">
        <div>
          <p className="eyebrow text-slate-400">Office</p>
          <p className="mt-1 text-[14px] font-bold text-ink">{intake.office_name}</p>
        </div>
        <div>
          <p className="eyebrow text-slate-400">Contact</p>
          <p className="mt-1 text-[14px] font-semibold text-ink">{intake.contact_name}</p>
        </div>
        <div>
          <p className="eyebrow text-slate-400">Phone</p>
          <a href={`tel:${String(intake.phone).replace(/[^\d+]/g, "")}`} className="mt-1 flex items-center gap-1.5 text-[14px] font-semibold text-brandsky-deep" data-testid={`admin-phone-${intake.intake_id}`}>
            <Phone className="h-3.5 w-3.5" /> {intake.phone}
          </a>
        </div>
        <div>
          <p className="eyebrow text-slate-400">Email</p>
          <a href={`mailto:${intake.email}`} className="mt-1 flex items-center gap-1.5 break-all text-[13.5px] font-semibold text-brandsky-deep" data-testid={`admin-email-${intake.intake_id}`}>
            <Mail className="h-3.5 w-3.5 shrink-0" /> {intake.email}
          </a>
        </div>
      </div>

      {intake.office_address ? (
        <p className="flex items-center gap-1.5 px-5 pb-3 text-[13px] text-slate-500">
          <MapPin className="h-3.5 w-3.5 text-brandpink" /> {intake.office_address}
        </p>
      ) : null}

      <div className="overflow-x-auto px-5 pb-2">
        <table className="w-full min-w-[560px] border-collapse text-left text-[13px]">
          <thead>
            <tr className="border-b border-ink/10">
              {["Qty", "Make & model", "Serial", "Service", "Problem"].map((h) => (
                <th key={h} className="py-2 pr-3 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {intake.items.map((it, i) => (
              <tr key={i} className="border-b border-ink/5 last:border-0" data-testid={`admin-item-${intake.intake_id}-${i}`}>
                <td className="py-2.5 pr-3 font-bold text-ink">{it.qty}</td>
                <td className="py-2.5 pr-3 font-semibold text-ink">{it.make_model}</td>
                <td className="py-2.5 pr-3 font-mono text-[12px] text-slate-500">{it.serial || "—"}</td>
                <td className="py-2.5 pr-3 text-slate-600">{it.service}</td>
                <td className="py-2.5 pr-3 text-slate-600">{it.problem}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {intake.notes ? (
        <p className="border-t border-ink/5 bg-paper/60 px-5 py-3 text-[13px] text-slate-600">
          <span className="font-bold text-ink">Notes:</span> {intake.notes}
        </p>
      ) : null}
    </div>
  );
}

function RouteSheet({ pending, onDone }) {
  useEffect(() => {
    const t = setTimeout(() => {
      window.print();
      onDone();
    }, 150);
    return () => clearTimeout(t);
  }, [onDone]);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const cell = { border: "1px solid #cbd5e1", padding: "4px 8px" };

  return (
    <div className="print-portal" style={{ padding: "20px 24px", fontFamily: "Arial, Helvetica, sans-serif", color: "#111" }} data-testid="route-sheet">
      <div style={{ borderBottom: "3px solid #16324F", paddingBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 6 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: -0.5 }}>JSL DENTAL REPAIR — VAN ROUTE SHEET</div>
          <div style={{ fontSize: 12, color: "#555" }}>
            {today} · {pending.length} stop{pending.length === 1 ? "" : "s"} · Efficient repairs, brilliant smiles.
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#16324F" }}>Questions? Call or text (716) 256-6630</div>
      </div>

      {pending.map((it, idx) => (
        <div key={it.intake_id} style={{ marginTop: 16, breakInside: "avoid" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F1F5F9", padding: "7px 10px", border: "1px solid #cbd5e1" }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>
              {idx + 1}. {it.office_name}
              <span style={{ fontWeight: 400, color: "#64748b", fontSize: 12 }}> · {it.delivery_method}</span>
            </div>
            <div style={{ border: "2px solid #16324F", width: 18, height: 18, borderRadius: 3, flexShrink: 0 }} title="Visited" />
          </div>
          <div style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderTop: 0, fontSize: 13 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px" }}>
              <span><strong>Contact:</strong> {it.contact_name}</span>
              <span><strong>Phone:</strong> {it.phone}</span>
              {it.office_address ? <span><strong>Address:</strong> {it.office_address}</span> : null}
              <span><strong>Ref:</strong> {it.intake_id}</span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12.5 }}>
              <thead>
                <tr>
                  {["Qty", "Make & model", "Serial", "Service", "Problem"].map((h) => (
                    <th key={h} style={{ ...cell, background: "#F8FAFC", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {it.items.map((x, i) => (
                  <tr key={i}>
                    <td style={cell}>{x.qty}</td>
                    <td style={cell}>{x.make_model}</td>
                    <td style={cell}>{x.serial || "—"}</td>
                    <td style={cell}>{x.service}</td>
                    <td style={cell}>{x.problem}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {it.notes ? <div style={{ marginTop: 6, fontSize: 12 }}><strong>Notes:</strong> {it.notes}</div> : null}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 24, fontSize: 11, color: "#777", borderTop: "1px solid #ddd", paddingTop: 8 }}>
        Printed {new Date().toLocaleString()} · JSL Dental Repair · Buffalo, NY — Jacob (716) 256-6630 · Jonah (716) 381-0305
      </div>
    </div>
  );
}

function Inbox({ token, onLogout }) {
  const [intakes, setIntakes] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/intakes`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        onLogout();
        toast.error("Session expired — sign in again.");
        return;
      }
      if (!res.ok) throw new Error("Could not load submissions");
      const data = await res.json();
      setIntakes(data.intakes);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [routeSheet, setRouteSheet] = useState(false);
  const [section, setSection] = useState("all");
  const pending = (intakes || []).filter((i) => (i.status || "new") === "new");

  const setIntakeStatus = async (ref, status) => {
    setIntakes((prev) => prev.map((i) => (i.intake_id === ref ? { ...i, status } : i)));
    const res = await fetch(`${API}/admin/intakes/${ref}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      toast.success(status === "completed" ? "Marked completed" : status === "to_do" ? "Moved to To do" : "Marked as not yet seen");
    } else {
      toast.error("Could not update status");
      load();
    }
  };

  const deleteIntake = async (ref) => {
    if (!window.confirm(`Delete submission ${ref}? This cannot be undone.`)) return;
    const res = await fetch(`${API}/admin/intakes/${ref}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      setIntakes((prev) => prev.filter((i) => i.intake_id !== ref));
      toast.success("Submission deleted");
    } else {
      toast.error("Could not delete");
    }
  };

  const printRoute = () => {
    if (!pending.length) {
      toast.error("No offices need to be seen right now.");
      return;
    }
    setRouteSheet(true);
  };

  return (
    <>
    <div className="min-h-screen bg-paper">
      <header className="border-b border-ink/10 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-auto" />
            <span className="font-display text-[15px] font-extrabold tracking-tight text-ink">INTAKE INBOX</span>
            <span className="font-mono rounded-full bg-brandpink px-2.5 py-0.5 text-[11px] font-bold text-white" data-testid="admin-intake-count">
              {intakes ? intakes.filter((i) => (i.status || "new") === "new").length : "…"}
            </span>
            <span className="text-[11px] font-bold text-slate-400">to see</span>
            <span className="font-mono rounded-full bg-brandleaf px-2.5 py-0.5 text-[11px] font-bold text-white" data-testid="admin-done-count">
              {intakes ? intakes.filter((i) => i.status === "done").length : "…"}
            </span>
            <span className="text-[11px] font-bold text-slate-400">done</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={printRoute}
              className="btn-lift inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-[12.5px] font-bold text-ink hover:border-ink/40"
              data-testid="admin-print-route-button"
            >
              <Printer className="h-3.5 w-3.5" /> Route sheet
            </button>
            <button
              onClick={load}
              className="btn-lift inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-[12.5px] font-bold text-ink hover:border-ink/40"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-5 flex flex-wrap gap-2" data-testid="intake-section-tabs">
          {[["all", "All"], ["new", "Not yet seen"], ["to_do", "To do"], ["completed", "Completed"]].map(([id, label]) => {
            const n = id === "all" ? (intakes || []).length : (intakes || []).filter((i) => (i.status || "new") === id).length;
            return (
              <button
                key={id}
                onClick={() => setSection(id)}
                data-testid={`intake-section-${id}`}
                className={`rounded-full px-4 py-2 text-[12.5px] font-bold transition-all ${
                  section === id ? "bg-ink text-white shadow-[0_8px_20px_rgba(22,50,79,0.25)]" : "border border-ink/15 bg-white text-ink hover:border-ink/40"
                }`}
              >
                {label} <span className="font-mono opacity-70">{intakes ? n : "…"}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold text-ink">Intake submissions</h2>
        </div>
        {loading && !intakes ? (
          <div className="flex items-center justify-center gap-3 py-24 text-slate-400" data-testid="admin-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading submissions…
          </div>
        ) : intakes && intakes.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-[24px] border border-dashed border-ink/20 py-24 text-center" data-testid="admin-empty">
            <span className="rounded-2xl bg-cloud p-4">
              <InboxIcon className="h-7 w-7 text-brandsky-deep" />
            </span>
            <p className="font-display mt-4 text-lg font-bold text-ink">No intakes yet</p>
            <p className="mt-1 text-[13.5px] text-slate-500">New form submissions will show up here automatically.</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-intakes-list">
            {intakes && (section === "all" ? intakes : intakes.filter((i) => (i.status || "new") === section)).map(
              (it) => <IntakeCard key={it.intake_id} intake={it} onStatus={setIntakeStatus} onDelete={deleteIntake} />
            )}
          </div>
        )}
        <p className="mt-8 text-center text-[12px] text-slate-400">
          Every submission is also emailed to jsldentalrepair@gmail.com · <a href="/" className="font-semibold hover:text-ink">← Back to the website</a>
        </p>
      </main>
    </div>
    {routeSheet && createPortal(<RouteSheet pending={pending} onDone={() => setRouteSheet(false)} />, document.body)}
    </>
  );
}

export function AuditTab({ token }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/audit-log`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setEntries(d.entries || []))
      .catch(() => setEntries([]));
  }, [token]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="audit-tab">
      <h2 className="font-display text-xl font-extrabold text-ink">Audit log — label requests</h2>
      {!entries ? (
        <p className="mt-10 text-slate-400">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="mt-10 rounded-2xl border border-dashed border-ink/20 p-10 text-center text-[14px] text-slate-500">
          No admin actions recorded yet. Approvals, declines, label uploads and status changes will appear here.
        </p>
      ) : (
        <ul className="mt-6 space-y-2" data-testid="audit-entries">
          {entries.map((e, i) => (
            <li key={i} className="rounded-xl border border-ink/10 bg-white px-4 py-3 text-[13px]">
              <span className="font-mono text-[11px] text-slate-400">{fmtDate(e.at)}</span>{" "}
              <span className="font-mono font-bold text-brandsky-deep">{e.request_id}</span>{" "}
              <span className="font-bold text-ink">{e.action}</span> — {e.by}
              {e.note ? <span className="text-slate-500"> · {String(e.note).slice(0, 120)}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("intakes");

  if (!token) {
    return <Login onLogin={(t) => setToken(t)} />;
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-ink/10 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-auto" />
            <span className="font-display text-[15px] font-extrabold tracking-tight text-ink">JSL ADMIN</span>
          </div>
          <nav className="flex flex-wrap items-center gap-1.5">
            {[["intakes", "Intake Inbox"], ["labels", "Label Requests"], ["audit", "Audit Log"]].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`rounded-full px-4 py-2 text-[12.5px] font-bold transition-all ${
                  tab === id ? "bg-ink text-white shadow-[0_8px_20px_rgba(22,50,79,0.25)]" : "border border-ink/15 bg-white text-ink hover:border-ink/40"
                }`}
              >
                {label}
              </button>
            ))}
            <button onClick={logout} data-testid="admin-logout-button" className="btn-lift ml-2 inline-flex items-center gap-2 rounded-full bg-brandpink px-4 py-2 text-[12.5px] font-bold text-white hover:bg-brandpink-deep">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </nav>
        </div>
      </header>
      {tab === "intakes" && <Inbox token={token} onLogout={logout} />}
      {tab === "labels" && <AdminLabels token={token} />}
      {tab === "audit" && <AuditTab token={token} />}
    </div>
  );
}